/**
 * Pure helpers behind the time-window pickers on the model pages: the
 * "Replicas over time" band's custom window box (TRA-1436) and the metrics
 * page's (TRA-1461). Kept out of the components so they are testable
 * without a DOM.
 */

export const MIN_WINDOW_HOURS = 1
/** The gateway keeps 7 days of replica samples, so the scaling api takes 1 to 168 hours. */
export const MAX_WINDOW_HOURS = 168
/** The metrics api takes up to 30 days, the gateway's rollup retention. */
export const MAX_METRICS_HOURS = 720

const WINDOW = /^(\d+)(h|hr|hrs|hour|hours|d|day|days)?$/

/**
 * The hours a typed window means, or null when it is not a whole number of
 * hours from 1 to `maxHours`. Case and spaces do not matter; a bare number
 * is hours.
 */
export function parseWindowHours(raw: string, maxHours: number): number | null {
  const match = WINDOW.exec(raw.toLowerCase().replace(/\s+/g, ''))
  if (match === null) return null
  const count = Number(match[1])
  const unit = match[2] ?? 'h'
  const hours = unit.startsWith('d') ? count * 24 : count
  return hours >= MIN_WINDOW_HOURS && hours <= maxHours ? hours : null
}

/** Windows past a day need the day in the label or every tick reads alike. */
export function formatTime(epoch: number, windowHours: number): string {
  if (windowHours > 24) {
    return new Date(epoch).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
    })
  }
  return new Date(epoch).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Whether a label inside the visible span of a chart needs the day: the
 * span is longer than a day, or some of it lies outside today in the
 * viewer's local time. A clock time alone would then read as today.
 */
export function spanNeedsDay(span: { start: number; end: number }, now: number): boolean {
  if (span.end - span.start > DAY_MS) return true
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  return span.start < today.getTime() || span.end >= tomorrow.getTime()
}

/**
 * A tick or hover time on a chart whose visible span is `span` (the zoomed
 * stretch, or the whole window). Inside today: a clock time. Not all today:
 * with the day. Over a day: the day and the hour, since a minute would
 * repeat on every tick.
 */
export function formatSpanTime(
  epoch: number,
  span: { start: number; end: number },
  now: number = Date.now(),
): string {
  if (!spanNeedsDay(span, now)) {
    return new Date(epoch).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }
  return new Date(epoch).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    ...(span.end - span.start > DAY_MS ? {} : { minute: '2-digit' }),
  })
}
